import {
  ledgerSchema,
  standingRuleListSchema,
  standingRuleSchema,
  userListSchema,
  wagerListSchema,
  wagerSchema,
  type ChallengeableUser,
  type CreateStandingRuleRequest,
  type Ledger,
  type ProposeWagerRequest,
  type StandingRuleResponse,
  type WagerAction,
  type WagerResponse,
} from '@gamble-chess/contracts';
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import { requestJson, requestNoContent } from './client.ts';
import { queryKeys, REFRESH_INTERVAL_MS } from './queryKeys.ts';

export function useLedger(): UseQueryResult<Ledger> {
  return useQuery({
    queryKey: queryKeys.ledger,
    queryFn: ({ signal }) =>
      requestJson('/api/ledger', ledgerSchema, 'Loading your ledger failed', { signal }),
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useRules(): UseQueryResult<readonly StandingRuleResponse[]> {
  return useQuery({
    queryKey: queryKeys.rules,
    queryFn: async ({ signal }) =>
      (
        await requestJson('/api/rules', standingRuleListSchema, 'Loading your rules failed', {
          signal,
        })
      ).rules,
  });
}

export function useCreateRule(): UseMutationResult<
  StandingRuleResponse,
  Error,
  CreateStandingRuleRequest
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request) =>
      requestJson('/api/rules', standingRuleSchema, 'Adding the rule failed. Please try again.', {
        method: 'POST',
        body: request,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

export function useDeleteRule(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) =>
      requestNoContent(
        `/api/rules/${encodeURIComponent(id)}`,
        'Deleting the rule failed. Please try again.',
        { method: 'DELETE' },
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.rules }),
  });
}

/** Other users, with the accounts a wager against them can name. */
export function useOpponents(): UseQueryResult<readonly ChallengeableUser[]> {
  return useQuery({
    queryKey: queryKeys.users,
    queryFn: async ({ signal }) =>
      (await requestJson('/api/users', userListSchema, 'Loading players failed', { signal })).users,
  });
}

/** Settled by the poller and answered by the other party, so it is refreshed like the ledger. */
export function useWagers(): UseQueryResult<readonly WagerResponse[]> {
  return useQuery({
    queryKey: queryKeys.wagers,
    queryFn: async ({ signal }) =>
      (await requestJson('/api/wagers', wagerListSchema, 'Loading your wagers failed', { signal }))
        .wagers,
    refetchInterval: REFRESH_INTERVAL_MS,
  });
}

export function useProposeWager(): UseMutationResult<WagerResponse, Error, ProposeWagerRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request) =>
      requestJson('/api/wagers', wagerSchema, 'Proposing the wager failed. Please try again.', {
        method: 'POST',
        body: request,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.wagers }),
  });
}

export interface WagerActionRequest {
  readonly id: string;
  readonly action: WagerAction;
}

export function useWagerAction(): UseMutationResult<WagerResponse, Error, WagerActionRequest> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, action }) =>
      requestJson(
        `/api/wagers/${encodeURIComponent(id)}/${action}`,
        wagerSchema,
        'Updating the wager failed. Please try again.',
        { method: 'POST' },
      ),
    // Whatever the outcome, the wager may have moved on; show its current state.
    onSettled: () => queryClient.invalidateQueries({ queryKey: queryKeys.wagers }),
  });
}
